/**
 * Stage (plateau) — personnages visibles sur une scène.
 * Pas d'héritage entre scènes : seuls `stageCharacters`, le locuteur du dialogue
 * courant (`characterId`) et les commandes show/hide de la scène comptent.
 * Les objets (type object) restent locaux à chaque scène.
 */

import type { Scenario, Scene, StageCharacter } from '../types';
import { findLocatedScene, getSceneById, type LocatedScene } from './scene-flow';
import {
  getDialoguePlacement,
  parseCharacterIdFromTarget,
  placementFromX,
  resolveDialogueMood,
} from './character-runtime';

const STAGE_RESET_TYPES = new Set([
  'phoneCall',
  'phoneChat',
  'phoneMail',
  'phoneMailInteract',
  'textOverlay',
  'chapterEnd',
  'confidenceGain',
  'recordCard',
]);

function applyIncrementalStage(scene: Scene, stage: Map<string, StageCharacter>): void {
  for (const el of scene.elements ?? []) {
    if (el.type !== 'character') continue;
    const id = parseCharacterIdFromTarget(el.id);
    if (!id) continue;
    stage.set(id, {
      characterId: id,
      placement: el.position?.x != null ? placementFromX(el.position.x) : 'centre',
    });
  }

  if (scene.type === 'dialogue' && scene.characterId && !scene.isInternal) {
    stage.set(scene.characterId, {
      characterId: scene.characterId,
      placement: getDialoguePlacement(scene, scene.elements),
      emotion: resolveDialogueMood(scene),
    });
  }

  if (scene.type === 'command') {
    if (scene.command === 'hideElement') {
      const id = parseCharacterIdFromTarget(scene.target ?? '');
      if (id) stage.delete(id);
    }
    if (scene.command === 'showElement') {
      const id = parseCharacterIdFromTarget(scene.target ?? '');
      if (id) {
        stage.set(id, {
          characterId: id,
          placement: stage.get(id)?.placement ?? 'centre',
          emotion: stage.get(id)?.emotion,
        });
      }
    }
  }
}

/**
 * Résout les personnages visibles pour une scène (sans héritage des scènes précédentes).
 */
export function resolveStageFromSpine(scenario: Scenario, sceneId: string): StageCharacter[] {
  const scene = getSceneById(scenario, sceneId);
  if (!scene) return [];

  if (STAGE_RESET_TYPES.has(scene.type)) return [];
  if (scene.clearStage) return [];
  if (scene.stageCharacters !== undefined) {
    return scene.stageCharacters.map((sc) => ({ ...sc }));
  }

  const stage = new Map<string, StageCharacter>();
  applyIncrementalStage(scene, stage);
  return [...stage.values()];
}

function isDialogueWithCharacter(scene: Scene): boolean {
  if (scene.type !== 'dialogue' || scene.isInternal) return false;
  if (scene.characterId) return true;
  return Boolean(scene.characterMood?.elementId);
}

function dialogueCharacterId(scene: Scene): string | undefined {
  if (scene.type !== 'dialogue') return undefined;
  if (scene.characterId) return scene.characterId;
  const target = scene.characterMood?.elementId;
  return target ? parseCharacterIdFromTarget(target) ?? undefined : undefined;
}

/** Inclut le contenu des branches d'un choix (ordre éditeur inline). */
function appendSceneForBackwardSearch(scene: Scene, out: Scene[]): void {
  if (scene.type !== 'choice') {
    out.push(scene);
    return;
  }
  for (const opt of scene.options ?? []) {
    for (const branchScene of opt.scenes ?? []) {
      appendBranchTree(branchScene, out);
    }
  }
}

function appendBranchTree(scene: Scene, out: Scene[]): void {
  out.push(scene);
  if (scene.type === 'choice') {
    for (const opt of scene.options ?? []) {
      for (const s of opt.scenes ?? []) {
        appendBranchTree(s, out);
      }
    }
  }
}

function findParentChoiceLocated(rootScenes: Scene[], targetId: string): LocatedScene | null {
  function search(scenes: Scene[]): LocatedScene | null {
    for (let i = 0; i < scenes.length; i++) {
      const scene = scenes[i]!;
      if (scene.type === 'choice') {
        for (const opt of scene.options ?? []) {
          if (opt.scenes?.some((s) => s.id === targetId)) {
            return { scene, parentArray: scenes, indexInParent: i };
          }
          if (opt.scenes?.length) {
            const nested = search(opt.scenes);
            if (nested) return nested;
          }
        }
      }
    }
    return null;
  }
  return search(rootScenes);
}

/** Scènes situées avant `located` dans l'ordre de lecture de l'éditeur inline. */
function collectPrecedingScenes(
  rootScenes: Scene[],
  located: LocatedScene,
  out: Scene[]
): void {
  const { parentArray, indexInParent, scene } = located;
  for (let i = 0; i < indexInParent; i++) {
    appendSceneForBackwardSearch(parentArray[i]!, out);
  }
  if (indexInParent === 0) {
    const parentChoice = findParentChoiceLocated(rootScenes, scene.id);
    if (parentChoice) {
      collectPrecedingScenes(rootScenes, parentChoice, out);
    }
  }
}

/**
 * Placement, émotion et personnage du dernier dialogue avec sprite avant `sceneId`
 * (parcours arrière dans l'ordre éditeur, y compris branches de choix).
 */
export function findLastDialogueStageDefaults(
  scenario: Scenario,
  sceneId: string
): Pick<StageCharacter, 'characterId' | 'placement' | 'emotion'> | null {
  const located = findLocatedScene(scenario.scenes, sceneId);
  if (!located) return null;

  const preceding: Scene[] = [];
  collectPrecedingScenes(scenario.scenes, located, preceding);

  for (let i = preceding.length - 1; i >= 0; i--) {
    const scene = preceding[i]!;
    if (!isDialogueWithCharacter(scene)) continue;
    const characterId = dialogueCharacterId(scene);
    if (!characterId) continue;
    return {
      characterId,
      placement: getDialoguePlacement(scene, scene.elements),
      emotion: resolveDialogueMood(scene),
    };
  }
  return null;
}

export function stageModeForScene(scene: Scene): 'auto' | 'custom' | 'cleared' {
  if (scene.clearStage) return 'cleared';
  if (scene.stageCharacters !== undefined) return 'custom';
  return 'auto';
}

/** Fusionne un patch scène en supprimant les overrides plateau quand ils sont explicitement vidés. */
export function mergeSceneStagePatch<T extends Scene>(scene: T, patch: Partial<T>): T {
  const next = { ...scene, ...patch };
  if (Object.prototype.hasOwnProperty.call(patch, 'stageCharacters') && patch.stageCharacters === undefined) {
    delete (next as Scene).stageCharacters;
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'clearStage') && patch.clearStage === undefined) {
    delete (next as Scene).clearStage;
  }
  return next;
}
